import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    FormControlLabel,
    Switch,
    CircularProgress,
    IconButton,
    Divider,
    RadioGroup,
    Radio,
    FormLabel,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import {
    Save as SaveIcon,
    ArrowBack as BackIcon,
    Publish as PublishIcon,
    FormatBold,
    FormatItalic,
    FormatListBulleted,
    FormatListNumbered,
    Code,
    Link as LinkIcon,
    History as HistoryIcon,
    AttachFile as AttachIcon,
    CloudUpload as UploadIcon,
    Delete as DeleteIcon,
    InsertDriveFile as FileIcon,
    Image as ImageIcon,
    VideoFile as VideoIcon,
    AudioFile as AudioIcon,
    PictureAsPdf as PdfIcon,
    Download as DownloadIcon,
    FolderOpen as BrowseIcon,
} from '@mui/icons-material';
import { useDataProvider, useNotify, usePermissions, useGetIdentity, useTranslate } from 'react-admin';
import { useNavigate, useParams } from 'react-router-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TiptapLink from '@tiptap/extension-link';
import WikiVersionHistory from './WikiVersionHistory';
import { canManageWikiArticles } from './wikiPermissions';
import { devLog } from '../../utils/devLog';

interface WikiCategory {
    id: number;
    name: string;
    color: string;
}

const WikiEditor: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const isNew = slug === 'new';

    const [title, setTitle] = useState('');
    const [slugValue, setSlugValue] = useState('');
    const [summary, setSummary] = useState('');
    const [status, setStatus] = useState<'draft' | 'published' | 'archived'>('draft');
    const [categoryId, setCategoryId] = useState<number | ''>('');
    const [categories, setCategories] = useState<WikiCategory[]>([]);
    const [isGlobal, setIsGlobal] = useState(false);
    const [originalCompanyId, setOriginalCompanyId] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [articleId, setArticleId] = useState<number | null>(null);
    const [versionNumber, setVersionNumber] = useState(1);
    const [currentVersionId, setCurrentVersionId] = useState<number | null>(null);
    const [versionHistoryOpen, setVersionHistoryOpen] = useState(false);
    const [attachments, setAttachments] = useState<any[]>([]);
    const [mediaPickerOpen, setMediaPickerOpen] = useState(false);
    const [mediaFiles, setMediaFiles] = useState<any[]>([]);
    const [mediaSearch, setMediaSearch] = useState('');
    const [mediaLoading, setMediaLoading] = useState(false);
    const [uploading, setUploading] = useState(false);

    const dataProvider = useDataProvider();
    const notify = useNotify();
    const navigate = useNavigate();
    const { permissions } = usePermissions();
    const { data: identity } = useGetIdentity();
    const translate = useTranslate();

    const canEditVisibility = permissions?.includes('ROLE_OPERATOR') || permissions?.includes('ROLE_PLATFORM_ADMIN');

    const editor = useEditor({
        extensions: [
            StarterKit,
            TiptapLink.configure({
                openOnClick: false,
            }),
        ],
        content: '<p>Beginnen Sie hier mit dem Schreiben...</p>',
        editorProps: {
            attributes: {
                class: 'prose prose-sm sm:prose lg:prose-lg xl:prose-2xl mx-auto focus:outline-none',
                style: 'min-height: 400px; padding: 16px;'
            },
        },
    });

    useEffect(() => {
        loadCategories();
        if (!isNew && slug) {
            loadArticle(slug);
        }
    }, [slug]);

    // Auto-generate slug from title
    useEffect(() => {
        if (isNew && title) {
            const generatedSlug = title
                .toLowerCase()
                .replace(/[^a-z0-9äöüß]+/g, '-')
                .replace(/^-|-$/g, '');
            setSlugValue(generatedSlug);
        }
    }, [title, isNew]);

    const loadCategories = async () => {
        try {
            const { data } = await dataProvider.getList('wiki_categories', {
                pagination: { page: 1, perPage: 100 },
                sort: { field: 'name', order: 'ASC' },
                filter: {}
            });
            setCategories(data);
        } catch (error) {
            notify('Fehler beim Laden der Kategorien', { type: 'error' });
        }
    };

    const loadArticle = async (articleSlug: string) => {
        setLoading(true);
        try {
            const { data } = await dataProvider.getList('wiki_articles', {
                pagination: { page: 1, perPage: 1 },
                sort: { field: 'id', order: 'ASC' },
                filter: { slug: articleSlug }
            });

            if (data.length === 0) {
                notify('Artikel nicht gefunden', { type: 'error' });
                navigate('/wiki');
                return;
            }

            const article = data[0];

            // Check permissions - author, oder eine der Wiki-Edit-Rollen (#304:
            // inkl. ROLE_COMPANY_ADMIN, sonst wurde der Company-Admin hier
            // rausgeworfen, obwohl der Backend-Patch ihn zulaesst).
            const isAuthor = identity?.id && article.createdBy && identity.id === article.createdBy;
            const canEdit = isAuthor || canManageWikiArticles(permissions);

            if (!canEdit) {
                notify('Sie haben keine Berechtigung, diesen Artikel zu bearbeiten', { type: 'error' });
                navigate('/wiki');
                return;
            }

            setArticleId(article.id);
            setTitle(article.title);
            setSlugValue(article.slug);
            setSummary(article.summary || '');
            setStatus(article.status);

            // Handle category - can be IRI string, plain ID string, or object
            if (article.category) {
                if (typeof article.category === 'string') {
                    // Try to extract ID from IRI like "/api/wiki_categories/123"
                    const match = article.category.match(/\/(\d+)$/);
                    if (match) {
                        setCategoryId(parseInt(match[1], 10));
                    } else {
                        // Plain ID string like "1"
                        const parsed = parseInt(article.category, 10);
                        setCategoryId(isNaN(parsed) ? '' : parsed);
                    }
                } else if (typeof article.category === 'object' && article.category.id) {
                    setCategoryId(article.category.id);
                }
            } else {
                setCategoryId('');
            }

            setIsGlobal(!article.companyId);
            setOriginalCompanyId(article.companyId);

            // Load attachments via dedicated endpoint
            try {
                const token = localStorage.getItem('token');
                const attRes = await fetch(`${process.env.REACT_APP_API_URL}/api/wiki-articles/${article.id}/attachments`, {
                    headers: { 'Authorization': `Bearer ${token}` },
                });
                if (attRes.ok) {
                    const attData = await attRes.json();
                    setAttachments(attData.attachments || []);
                }
            } catch (err) {
                console.error('[WikiEditor] Failed to load attachments:', err);
            }

            // Load current version content
            if (article.currentVersion) {
                let versionId: number;

                if (typeof article.currentVersion === 'object') {
                    versionId = article.currentVersion.id;
                } else if (typeof article.currentVersion === 'string') {
                    // Extract ID from IRI like "/api/wiki_versions/123"
                    const match = article.currentVersion.match(/\/(\d+)$/);
                    versionId = match ? parseInt(match[1], 10) : parseInt(article.currentVersion, 10);
                } else {
                    versionId = parseInt(article.currentVersion, 10);
                }

                const { data: versionData } = await dataProvider.getOne('wiki_versions', { id: versionId });
                if (versionData.content && editor) {
                    editor.commands.setContent(versionData.content);
                }
                setVersionNumber(versionData.versionNumber || 1);
                setCurrentVersionId(versionId);
            } else {
                // No currentVersion set - try to load the latest version
                try {
                    const { data: versions } = await dataProvider.getList('wiki_versions', {
                        pagination: { page: 1, perPage: 1 },
                        sort: { field: 'versionNumber', order: 'DESC' },
                        filter: { article: `/api/wiki_articles/${article.id}` }
                    });

                    if (versions.length > 0) {
                        const latestVersion = versions[0];
                        if (latestVersion.content && editor) {
                            editor.commands.setContent(latestVersion.content);
                        }
                        setVersionNumber(latestVersion.versionNumber);
                        setCurrentVersionId(latestVersion.id);
                    } else {
                        setVersionNumber(0);
                    }
                } catch (err) {
                    console.error('Error loading latest version:', err);
                    setVersionNumber(0);
                }
            }
        } catch (error) {
            notify('Fehler beim Laden des Artikels', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (publishNow: boolean = false) => {
        if (!title.trim()) {
            notify('Bitte geben Sie einen Titel ein', { type: 'warning' });
            return;
        }

        if (!slugValue.trim()) {
            notify('Bitte geben Sie einen URL-Slug ein', { type: 'warning' });
            return;
        }

        if (!editor) {
            notify('Editor nicht bereit', { type: 'error' });
            return;
        }

        setSaving(true);
        try {
            const articleData: any = {
                title,
                slug: slugValue,
                summary,
                status: publishNow ? 'published' : status,
            };

            if (categoryId) {
                articleData.category = `/api/wiki_categories/${categoryId}`;
            }

            let article;
            if (isNew) {
                const { data } = await dataProvider.create('wiki_articles', { data: articleData });
                article = data;
                setArticleId(article.id);
            } else if (articleId) {
                const { data } = await dataProvider.update('wiki_articles', {
                    id: articleId,
                    data: articleData,
                    previousData: { id: articleId }
                });
                article = data;
            }

            if (article && canEditVisibility) {
                const targetCompanyId = isGlobal
                    ? null
                    : (originalCompanyId ?? (identity as any)?.companyId ?? null);
                const scopeResponse = await fetch(
                    `${process.env.REACT_APP_API_URL}/api/wiki_articles/${article.id}/scope`,
                    {
                        method: 'PATCH',
                        headers: {
                            'Authorization': `Bearer ${localStorage.getItem('token')}`,
                            'Content-Type': 'application/merge-patch+json',
                        },
                        body: JSON.stringify({ companyId: targetCompanyId }),
                    }
                );

                if (!scopeResponse.ok) {
                    throw new Error('Sichtbarkeit konnte nicht geändert werden');
                }

                setOriginalCompanyId(targetCompanyId);
            }

            if (article) {
                // Create new version
                const content = editor.getHTML();
                const newVersionNumber = isNew ? 1 : versionNumber + 1;
                const versionData = {
                    article: `/api/wiki_articles/${article.id}`,
                    versionNumber: newVersionNumber,
                    content,
                    changeComment: isNew ? 'Erste Version' : 'Aktualisierung'
                };

                const { data: version } = await dataProvider.create('wiki_versions', { data: versionData });

                // Set as current version
                await dataProvider.update('wiki_articles', {
                    id: article.id,
                    data: { currentVersion: `/api/wiki_versions/${version.id}` },
                    previousData: { id: article.id }
                });

                setVersionNumber(newVersionNumber);
                setCurrentVersionId(version.id);

                // Save attachments LAST via dedicated PUT endpoint (replaces entire collection)
                const token = localStorage.getItem('token');
                const apiUrl = process.env.REACT_APP_API_URL;
                const attResponse = await fetch(`${apiUrl}/api/wiki-articles/${article.id}/attachments`, {
                    method: 'PUT',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ attachmentIds: attachments.map(a => a.id) }),
                });
                devLog('[WikiEditor] Attachment PUT response:', attResponse.status, await attResponse.clone().text());
            }

            const savedSlug = article?.slug ?? slugValue;
            const slugAdjusted = savedSlug !== slugValue;
            if (slugAdjusted) {
                setSlugValue(savedSlug);
            }

            notify(
                slugAdjusted
                    ? `Der URL-Slug "${slugValue}" war bereits vergeben — der Artikel liegt unter "${savedSlug}"`
                    : isNew ? 'Artikel erstellt' : 'Artikel aktualisiert',
                { type: slugAdjusted ? 'info' : 'success' }
            );

            if (isNew) {
                navigate(`/wiki/${savedSlug}/edit`);
            }
        } catch (error: any) {
            // Parse API Platform error response for better user feedback
            let errorMessage = 'Fehler beim Speichern';

            if (error?.body) {
                // API Platform validation errors
                if (error.body['hydra:description']) {
                    errorMessage = error.body['hydra:description'];
                } else if (error.body.detail) {
                    errorMessage = error.body.detail;
                } else if (error.body.message) {
                    errorMessage = error.body.message;
                }

                // Check for unique constraint violation on slug
                if (errorMessage.includes('slug') || errorMessage.includes('Duplicate') || errorMessage.includes('unique') || errorMessage.includes('1062')) {
                    errorMessage = `Ein Artikel mit dem URL-Slug "${slugValue}" existiert bereits. Bitte wählen Sie einen anderen Slug.`;
                }
            } else if (error?.message) {
                errorMessage = error.message;
            }

            notify(errorMessage, { type: 'error' });
            console.error('Save error details:', error);
            // Don't close dialog - let user fix the error
        } finally {
            setSaving(false);
        }
    };

    const handleVersionRestore = async (versionId: number, versionNum: number, content: string) => {
        if (!editor || !articleId) return;

        try {
            // Set editor content to restored version
            editor.commands.setContent(content);

            // Update state
            setVersionNumber(versionNum);
            setCurrentVersionId(versionId);

            // Update article to point to this version
            await dataProvider.update('wiki_articles', {
                id: articleId,
                data: { currentVersion: `/api/wiki_versions/${versionId}` },
                previousData: { id: articleId }
            });

            notify('Version wiederhergestellt', { type: 'success' });
        } catch (error: any) {
            notify(error?.message || 'Fehler beim Wiederherstellen', { type: 'error' });
        }
    };

    const getFileIcon = (mimeType?: string) => {
        if (!mimeType) return <FileIcon />;
        if (mimeType.startsWith('image/')) return <ImageIcon color="primary" />;
        if (mimeType.startsWith('video/')) return <VideoIcon color="secondary" />;
        if (mimeType.startsWith('audio/')) return <AudioIcon color="info" />;
        if (mimeType === 'application/pdf') return <PdfIcon color="error" />;
        return <FileIcon color="action" />;
    };

    const formatFileSize = (bytes: number | string) => {
        const b = typeof bytes === 'string' ? parseInt(bytes) : bytes;
        if (!b || isNaN(b)) return '–';
        if (b < 1024) return `${b} B`;
        if (b < 1048576) return `${(b / 1024).toFixed(1)} KB`;
        return `${(b / 1048576).toFixed(1)} MB`;
    };

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;

        setUploading(true);
        try {
            const token = localStorage.getItem('token');
            for (const file of Array.from(files)) {
                const formData = new FormData();
                formData.append('file', file);
                const response = await fetch(`${process.env.REACT_APP_API_URL}/api/media/upload`, {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}` },
                    body: formData,
                });
                if (response.ok) {
                    const result = await response.json();
                    const { data: mediaFile } = await dataProvider.getOne('media_files', { id: result.file.id });
                    setAttachments(prev => [...prev, mediaFile]);
                } else {
                    notify(`Fehler beim Hochladen von ${file.name}`, { type: 'error' });
                }
            }
        } catch (err) {
            notify('Fehler beim Hochladen', { type: 'error' });
        } finally {
            setUploading(false);
            e.target.value = '';
        }
    };

    const handleRemoveAttachment = (fileId: number) => {
        setAttachments(prev => prev.filter(a => a.id !== fileId));
    };

    const loadMediaFiles = async (search?: string) => {
        setMediaLoading(true);
        try {
            const filter: any = {};
            if (search) filter.originalFilename = search;
            const { data } = await dataProvider.getList('media_files', {
                pagination: { page: 1, perPage: 50 },
                sort: { field: 'createdAt', order: 'DESC' },
                filter
            });
            setMediaFiles(data);
        } catch { /* ignore */ }
        finally { setMediaLoading(false); }
    };

    const handleOpenMediaPicker = () => {
        setMediaPickerOpen(true);
        loadMediaFiles();
    };

    const handleSelectMedia = (file: any) => {
        if (!attachments.find(a => a.id === file.id)) {
            setAttachments(prev => [...prev, file]);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!editor) {
        return null;
    }

    return (
        <Box>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Button startIcon={<BackIcon />} onClick={() => navigate('/wiki')}>
                        Zurück
                    </Button>
                    <Typography variant="h4">
                        {isNew ? 'Neuer Artikel' : 'Artikel bearbeiten'}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                        variant="outlined"
                        startIcon={<SaveIcon />}
                        onClick={() => handleSave(false)}
                        disabled={saving}
                    >
                        {saving ? 'Speichern...' : 'Speichern'}
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<PublishIcon />}
                        onClick={() => handleSave(true)}
                        disabled={saving}
                    >
                        Speichern & Veröffentlichen
                    </Button>
                </Box>
            </Box>

            <Paper sx={{ p: 3 }}>
                {/* Basic Info */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 3 }}>
                    <TextField
                        label="Titel"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        fullWidth
                        required
                    />

                    <TextField
                        label="URL-Slug"
                        value={slugValue}
                        onChange={(e) => setSlugValue(e.target.value)}
                        fullWidth
                        required
                        helperText="URL-freundlicher Name (wird automatisch generiert)"
                    />

                    <TextField
                        label="Zusammenfassung"
                        value={summary}
                        onChange={(e) => setSummary(e.target.value)}
                        multiline
                        rows={2}
                        fullWidth
                        helperText="Kurze Beschreibung für die Artikelübersicht"
                    />

                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <FormControl sx={{ minWidth: 200 }}>
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={status}
                                label="Status"
                                onChange={(e) => setStatus(e.target.value as any)}
                            >
                                <MenuItem value="draft">Entwurf</MenuItem>
                                <MenuItem value="published">Veröffentlicht</MenuItem>
                                <MenuItem value="archived">Archiviert</MenuItem>
                            </Select>
                        </FormControl>

                        <FormControl sx={{ minWidth: 200 }}>
                            <InputLabel>Kategorie</InputLabel>
                            <Select
                                value={categoryId}
                                label="Kategorie"
                                onChange={(e) => setCategoryId(e.target.value as number)}
                            >
                                <MenuItem value="">Keine</MenuItem>
                                {categories.map((cat) => (
                                    <MenuItem key={cat.id} value={cat.id}>
                                        <Chip label={cat.name} size="small" sx={{ backgroundColor: cat.color }} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {canEditVisibility && (
                            <FormControl component="fieldset">
                                <FormLabel component="legend">{translate('wiki.editor.visibility')}</FormLabel>
                                <RadioGroup
                                    value={isGlobal ? 'global' : 'company'}
                                    onChange={(e) => setIsGlobal(e.target.value === 'global')}
                                >
                                    <FormControlLabel
                                        value="company"
                                        control={<Radio />}
                                        label={translate('wiki.editor.visibilityCompany')}
                                    />
                                    <FormControlLabel
                                        value="global"
                                        control={<Radio />}
                                        label={translate('wiki.editor.visibilityGlobal')}
                                    />
                                </RadioGroup>
                            </FormControl>
                        )}
                    </Box>
                </Box>

                {/* Editor Toolbar */}
                <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                        Inhalt
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 1, mb: 1 }}>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            <IconButton
                                size="small"
                                onClick={() => editor.chain().focus().toggleBold().run()}
                                color={editor.isActive('bold') ? 'primary' : 'default'}
                            >
                                <FormatBold />
                            </IconButton>
                            <IconButton
                                size="small"
                                onClick={() => editor.chain().focus().toggleItalic().run()}
                                color={editor.isActive('italic') ? 'primary' : 'default'}
                            >
                                <FormatItalic />
                            </IconButton>
                            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                            <IconButton
                                size="small"
                                onClick={() => editor.chain().focus().toggleBulletList().run()}
                                color={editor.isActive('bulletList') ? 'primary' : 'default'}
                            >
                                <FormatListBulleted />
                            </IconButton>
                            <IconButton
                                size="small"
                                onClick={() => editor.chain().focus().toggleOrderedList().run()}
                                color={editor.isActive('orderedList') ? 'primary' : 'default'}
                            >
                                <FormatListNumbered />
                            </IconButton>
                            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                            <IconButton
                                size="small"
                                onClick={() => editor.chain().focus().toggleCodeBlock().run()}
                                color={editor.isActive('codeBlock') ? 'primary' : 'default'}
                            >
                                <Code />
                            </IconButton>
                            <Button
                                size="small"
                                variant={editor.isActive('heading', { level: 1 }) ? 'contained' : 'outlined'}
                                onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                            >
                                H1
                            </Button>
                            <Button
                                size="small"
                                variant={editor.isActive('heading', { level: 2 }) ? 'contained' : 'outlined'}
                                onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                            >
                                H2
                            </Button>
                            <Button
                                size="small"
                                variant={editor.isActive('heading', { level: 3 }) ? 'contained' : 'outlined'}
                                onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                            >
                                H3
                            </Button>
                        </Box>
                    </Paper>

                    {/* Editor Content */}
                    <Paper
                        variant="outlined"
                        sx={{
                            minHeight: 400,
                            p: 2,
                            '& .ProseMirror': {
                                outline: 'none',
                                minHeight: 400,
                                '& h1': { fontSize: '2em', fontWeight: 'bold', marginTop: '0.67em', marginBottom: '0.67em' },
                                '& h2': { fontSize: '1.5em', fontWeight: 'bold', marginTop: '0.75em', marginBottom: '0.75em' },
                                '& h3': { fontSize: '1.17em', fontWeight: 'bold', marginTop: '0.83em', marginBottom: '0.83em' },
                                '& p': { marginTop: '1em', marginBottom: '1em' },
                                '& ul, & ol': { paddingLeft: '2em', marginTop: '1em', marginBottom: '1em' },
                                '& code': { backgroundColor: '#f5f5f5', padding: '2px 4px', borderRadius: '3px', fontFamily: 'monospace' },
                                '& pre': { backgroundColor: '#f5f5f5', padding: '1em', borderRadius: '4px', overflow: 'auto' },
                            }
                        }}
                    >
                        <EditorContent editor={editor} />
                    </Paper>
                </Box>

                {/* Attachments Section */}
                <Divider sx={{ my: 3 }} />
                <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AttachIcon fontSize="small" />
                            Dateianhänge ({attachments.length})
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<BrowseIcon />}
                                onClick={handleOpenMediaPicker}
                            >
                                Aus Medienbibliothek
                            </Button>
                            <Button
                                size="small"
                                variant="outlined"
                                component="label"
                                startIcon={uploading ? <CircularProgress size={16} /> : <UploadIcon />}
                                disabled={uploading}
                            >
                                {uploading ? 'Laden...' : 'Datei hochladen'}
                                <input type="file" hidden multiple onChange={handleFileUpload} />
                            </Button>
                        </Box>
                    </Box>

                    {attachments.length > 0 ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {attachments.map((file) => (
                                <Paper key={file.id} variant="outlined" sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 2 }}>
                                    {getFileIcon(file.mimeType)}
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="body2" noWrap>{file.originalFilename}</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {formatFileSize(file.fileSize || file.size)} · {file.fileType || file.mimeType}
                                        </Typography>
                                    </Box>
                                    {file.uuid && (
                                        <IconButton
                                            size="small"
                                            title="Herunterladen"
                                            onClick={async () => {
                                                try {
                                                    const tk = localStorage.getItem('token');
                                                    const resp = await fetch(
                                                        `${process.env.REACT_APP_API_URL}/api/media/download/${file.uuid}`,
                                                        { headers: { 'Authorization': `Bearer ${tk}` } }
                                                    );
                                                    if (!resp.ok) throw new Error();
                                                    const blob = await resp.blob();
                                                    const url = window.URL.createObjectURL(blob);
                                                    const a = document.createElement('a');
                                                    a.href = url;
                                                    a.download = file.originalFilename || 'download';
                                                    a.click();
                                                    window.URL.revokeObjectURL(url);
                                                } catch { notify('Fehler beim Herunterladen', { type: 'error' }); }
                                            }}
                                        >
                                            <DownloadIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                    <IconButton size="small" color="error" onClick={() => handleRemoveAttachment(file.id)} title="Entfernen">
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Paper>
                            ))}
                        </Box>
                    ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                            Keine Anhänge. Laden Sie Dateien hoch oder wählen Sie aus der Medienbibliothek.
                        </Typography>
                    )}
                </Box>

                {!isNew && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                            Version {versionNumber}
                        </Typography>
                        <Button
                            size="small"
                            startIcon={<HistoryIcon />}
                            onClick={() => setVersionHistoryOpen(true)}
                        >
                            Versionsverlauf
                        </Button>
                    </Box>
                )}
            </Paper>

            {/* Media Picker Dialog */}
            <Dialog open={mediaPickerOpen} onClose={() => setMediaPickerOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Datei aus Medienbibliothek auswählen</DialogTitle>
                <DialogContent>
                    <TextField
                        placeholder="Datei suchen..."
                        fullWidth
                        size="small"
                        value={mediaSearch}
                        onChange={(e) => { setMediaSearch(e.target.value); loadMediaFiles(e.target.value); }}
                        sx={{ mb: 2, mt: 1 }}
                    />
                    {mediaLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
                    ) : mediaFiles.length === 0 ? (
                        <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                            Keine Dateien gefunden
                        </Typography>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 400, overflow: 'auto' }}>
                            {mediaFiles.map((file) => {
                                const alreadyAdded = attachments.some(a => a.id === file.id);
                                return (
                                    <Paper
                                        key={file.id}
                                        variant="outlined"
                                        sx={{
                                            p: 1.5, display: 'flex', alignItems: 'center', gap: 2,
                                            cursor: alreadyAdded ? 'default' : 'pointer',
                                            opacity: alreadyAdded ? 0.5 : 1,
                                            '&:hover': alreadyAdded ? {} : { bgcolor: 'action.hover' },
                                        }}
                                        onClick={() => !alreadyAdded && handleSelectMedia(file)}
                                    >
                                        {getFileIcon(file.mimeType)}
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography variant="body2" noWrap>{file.originalFilename}</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {formatFileSize(file.fileSize || file.size)} · {new Date(file.createdAt).toLocaleDateString('de-DE')}
                                            </Typography>
                                        </Box>
                                        {alreadyAdded ? (
                                            <Chip label="Hinzugefügt" size="small" color="success" />
                                        ) : (
                                            <Button size="small" variant="outlined">Auswählen</Button>
                                        )}
                                    </Paper>
                                );
                            })}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setMediaPickerOpen(false)}>Schließen</Button>
                </DialogActions>
            </Dialog>

            {/* Version History Dialog */}
            {!isNew && articleId && (
                <WikiVersionHistory
                    open={versionHistoryOpen}
                    onClose={() => setVersionHistoryOpen(false)}
                    articleId={articleId}
                    currentVersionId={currentVersionId || undefined}
                    onRestore={handleVersionRestore}
                />
            )}
        </Box>
    );
};

export default WikiEditor;
